import { stringify } from 'csv-stringify/browser/esm/sync';
import DOMPurify from 'dompurify';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { create } from 'zustand';

import { useFeatureFlag } from '@bonusly/who-am-i';

import { loadAdminNominationPeriod } from '../../queries/nominations';
import { AdminNominationPeriodDetail, Nomination, NominationQuestion } from '../../schemas/nominations';
import { NOMINATION_MODAL_QUERY_PARAM } from './constants';

// Module-level variable to ensure hook only runs once across component remounts
let hasInitializedGlobally = false;

const LEGACY_CSV_HEADER_KEYS = ['nomination', 'nominee', 'submission', 'message'];

const triggerCsvDownload = (csv: string, fileName: string) => {
	const blob = new Blob([csv], { type: 'text/csv' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = `${fileName}.csv`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

const stripHtml = (value: string): string => DOMPurify.sanitize(value, { ALLOWED_TAGS: [] });

const formatAnswerForCsv = (nomination: Nomination, question: NominationQuestion): string => {
	const answer = nomination.answers.find((a) => a.questionId === question.id);
	if (!answer || answer.answer.length === 0) return '';
	// Multi-choice answers are joined with `; ` so each cell stays a single CSV value.
	return answer.answer.map(stripHtml).join('; ');
};

// Stable sort by nominee display name (case- and accent-insensitive). Display names are typically
// "First Last", so this naturally orders rows by first name without any parsing. Falls back to the
// embedded group's display name when nominatedUser is absent (group-mode nominations).
const nomineeDisplayName = (n: Nomination): string => n.nominatedGroup?.displayName ?? n.nominatedUser?.displayName ?? '';

export const sortNominationsByNomineeDisplayName = (nominations: readonly Nomination[]): Nomination[] => {
	return [...nominations].sort((a, b) => nomineeDisplayName(a).localeCompare(nomineeDisplayName(b), undefined, { sensitivity: 'base' }));
};

const buildLegacyCsv = (data: AdminNominationPeriodDetail, t: (key: string) => string): string => {
	const rows = sortNominationsByNomineeDisplayName(data.nominations).map((nomination) => [
		data.name,
		nomineeDisplayName(nomination),
		nomination.nominator.displayName,
		stripHtml(nomination.reason),
	]);

	return stringify(rows, {
		columns: LEGACY_CSV_HEADER_KEYS.map((key) => t(key)),
		header: true,
	});
};

const buildQuestionsCsv = (data: AdminNominationPeriodDetail, t: (key: string) => string, legacyReasonHeading: string): string => {
	// Periods created before custom questions launched have no `questions` and rely on the
	// free-text `reason`. Synthesize a single text question so the CSV layout stays consistent.
	const questions: NominationQuestion[] =
		data.questions.length > 0
			? data.questions
			: [{ id: '__legacy_reason__', questionText: legacyReasonHeading, questionType: 'text', options: [], isRequired: false }];

	const isLegacyOnly = data.questions.length === 0;
	const isGroupPeriod = data.nomineeType === 'group';

	const headers = [t('nominee'), ...(isGroupPeriod ? [t('groupType')] : []), t('nominator'), t('dateUtc'), ...questions.map((q) => q.questionText)];

	const rows = sortNominationsByNomineeDisplayName(data.nominations).map((nomination) => [
		nomineeDisplayName(nomination),
		...(isGroupPeriod ? [nomination.nominatedGroup?.groupType ?? ''] : []),
		nomination.nominator.displayName,
		nomination.createdAt.toISOString(),
		...questions.map((q) => (isLegacyOnly ? stripHtml(nomination.reason) : formatAnswerForCsv(nomination, q))),
	]);

	return stringify(rows, {
		columns: headers,
		header: true,
	});
};

export const useDownloadNominationPeriodCsv = (id: string) => {
	const { t } = useTranslation('nominations', { keyPrefix: 'csv' });
	const { t: tDetail } = useTranslation('nominations', { keyPrefix: 'detail' });
	const customQuestionsEnabled = useFeatureFlag('nominations-custom-questions');

	return useCallback(async () => {
		const data = await loadAdminNominationPeriod(id);
		const csv = customQuestionsEnabled ? buildQuestionsCsv(data, t, tDetail('legacyReasonHeading')) : buildLegacyCsv(data, t);

		triggerCsvDownload(csv, data.name);
	}, [id, t, tDetail, customQuestionsEnabled]);
};

export const useInitNominationModal = () => {
	const [params] = useSearchParams();
	const onOpenNominationPeriod = useNominationModal((state) => state.onOpenNominationPeriod);

	useEffect(() => {
		// Skip if already initialized or no relevant query param
		if (hasInitializedGlobally || !params.has(NOMINATION_MODAL_QUERY_PARAM)) {
			return;
		}

		hasInitializedGlobally = true;
		onOpenNominationPeriod(params.get(NOMINATION_MODAL_QUERY_PARAM));
	}, [params, onOpenNominationPeriod]);
};

type UseNominationModalValue = {
	id: string | null;
	isOpen: boolean;
	onOpen: () => void;
	onOpenNominationPeriod: (id: string | null) => void;
	onClose: () => void;
};

export const useNominationModal = create<UseNominationModalValue>((set) => ({
	id: null,
	isOpen: false,
	onOpen: () => set({ id: null, isOpen: true }),
	onOpenNominationPeriod: (id) => set({ id, isOpen: true }),
	onClose: () => set({ id: null, isOpen: false }),
}));
